import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  addCompanyInventorySpec,
  createCompanyInventory,
  fetchCompanyInventory,
  fetchCompanyInventoryMasters,
} from '../../api/companyInventory';
import {
  CompanyInventoryItem,
  CompanyInventoryMaster,
  InventoryItemKind,
  formatInventoryQty,
  inventoryKindLabel,
} from '../../types/companyInventory';
import { notifyError } from '../../utils/notify';
import './CompanyInventoryPickerModal.css';

type KindFilter = 'ALL' | InventoryItemKind;

const KIND_OPTIONS: { value: KindFilter; label: string }[] = [
  { value: 'ALL', label: '전체' },
  { value: 'MATERIAL', label: inventoryKindLabel('MATERIAL') },
  { value: 'PART', label: inventoryKindLabel('PART') },
  { value: 'FINISHED', label: inventoryKindLabel('FINISHED') },
];

type Props = {
  open: boolean;
  items: CompanyInventoryItem[];
  masters: CompanyInventoryMaster[];
  initialKind?: KindFilter;
  title?: string;
  onClose: () => void;
  onSelect: (item: CompanyInventoryItem) => void;
  onListsUpdated?: (
    items: CompanyInventoryItem[],
    masters: CompanyInventoryMaster[]
  ) => void;
};

function CompanyInventoryPickerModal({
  open,
  items,
  masters,
  initialKind = 'ALL',
  title = '발주 품목 선택',
  onClose,
  onSelect,
  onListsUpdated,
}: Props) {
  const [kindFilter, setKindFilter] = useState<KindFilter>(initialKind);
  const [keyword, setKeyword] = useState('');
  const [selectingId, setSelectingId] = useState<number | null>(null);
  const [showQuick, setShowQuick] = useState(false);
  const [quickMode, setQuickMode] = useState<'existing' | 'new'>('new');
  const [quickMasterId, setQuickMasterId] = useState<number | null>(null);
  const [quickKind, setQuickKind] = useState<InventoryItemKind>('MATERIAL');
  const [quickName, setQuickName] = useState('');
  const [quickSpec, setQuickSpec] = useState('');
  const [quickUnit, setQuickUnit] = useState('EA');
  const [quickSaving, setQuickSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setKeyword('');
      setSelectingId(null);
      setShowQuick(false);
      setQuickMode('new');
      setQuickMasterId(null);
      setQuickName('');
      setQuickSpec('');
      setQuickUnit('EA');
      setQuickKind('MATERIAL');
      return;
    }
    setKindFilter(initialKind);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectingId == null && !quickSaving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, selectingId, quickSaving, initialKind]);

  const filteredMasters = useMemo(() => {
    if (kindFilter === 'ALL') return masters;
    return masters.filter((m) => m.itemKind === kindFilter);
  }, [masters, kindFilter]);

  const filtered = useMemo(() => {
    const byKind =
      kindFilter === 'ALL' ? items : items.filter((row) => row.itemKind === kindFilter);
    const q = keyword.trim().toLowerCase();
    if (!q) return byKind;
    return byKind.filter((row) => {
      const hay = [
        inventoryKindLabel(row.itemKind),
        row.itemName,
        row.specText,
        row.unit,
        row.itemCode,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [items, kindFilter, keyword]);

  const pickItem = async (item: CompanyInventoryItem) => {
    try {
      setSelectingId(item.inventoryId);
      onSelect(item);
      onClose();
    } finally {
      setSelectingId(null);
    }
  };

  const handleQuickCreate = async () => {
    if (quickMode === 'existing') {
      if (quickMasterId == null) {
        notifyError('기존 품명을 선택해 주세요.');
        return;
      }
      if (!quickSpec.trim()) {
        notifyError('추가할 규격을 입력해 주세요.');
        return;
      }
    } else if (!quickName.trim()) {
      notifyError('품명을 입력해 주세요.');
      return;
    }

    try {
      setQuickSaving(true);
      let created: CompanyInventoryItem;

      if (quickMode === 'existing') {
        const master = masters.find((m) => m.masterId === quickMasterId);
        if (!master) {
          notifyError('선택한 품명을 찾을 수 없습니다.');
          return;
        }
        created = await addCompanyInventorySpec(master.masterId, {
          itemKind: master.itemKind,
          itemName: master.itemName,
          specText: quickSpec.trim(),
          stockQty: 0,
          unit: quickUnit.trim() || master.defaultUnit || undefined,
        });
      } else {
        const existingMaster = masters.find(
          (m) => m.itemKind === quickKind && m.itemName.trim() === quickName.trim()
        );
        created = existingMaster
          ? await addCompanyInventorySpec(existingMaster.masterId, {
              itemKind: quickKind,
              itemName: quickName.trim(),
              specText: quickSpec.trim(),
              stockQty: 0,
              unit: quickUnit.trim() || undefined,
            })
          : await createCompanyInventory({
              itemKind: quickKind,
              itemName: quickName.trim(),
              specText: quickSpec.trim(),
              stockQty: 0,
              unit: quickUnit.trim() || undefined,
            });
      }

      const [nextInventory, nextMasters] = await Promise.all([
        fetchCompanyInventory(),
        fetchCompanyInventoryMasters(),
      ]);
      onListsUpdated?.(nextInventory, nextMasters);
      const resolved =
        nextInventory.find((row) => row.inventoryId === created.inventoryId) ?? created;
      onSelect(resolved);
      onClose();
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '품목 등록에 실패했습니다.'
      );
    } finally {
      setQuickSaving(false);
    }
  };

  if (!open) return null;

  const busy = selectingId != null || quickSaving;

  return createPortal(
    <div
      className="inv-picker-modal__backdrop"
      role="presentation"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        className="inv-picker-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="inv-picker-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="inv-picker-modal__head">
          <div>
            <p className="inv-picker-modal__eyebrow">재고 품목</p>
            <h3 id="inv-picker-modal-title">{title}</h3>
            <p className="inv-picker-modal__sub">
              구분으로 좁힌 뒤 목록에서 선택하세요. 없으면 빠른 등록으로 바로 추가할 수 있습니다.
            </p>
          </div>
          <button
            type="button"
            className="inv-picker-modal__close"
            aria-label="닫기"
            disabled={busy}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="inv-picker-modal__filters">
          {KIND_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`inv-picker-modal__chip${kindFilter === opt.value ? ' is-active' : ''}`}
              disabled={busy}
              onClick={() => setKindFilter(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="inv-picker-modal__toolbar">
          <input
            type="search"
            className="inv-picker-modal__search"
            placeholder="품명·규격·단위로 검색"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            disabled={busy}
          />
          <span className="inv-picker-modal__count">
            {filtered.length}/{items.length}건
          </span>
          <button
            type="button"
            className="inv-picker-modal__quick-toggle"
            disabled={busy}
            onClick={() => setShowQuick((v) => !v)}
          >
            {showQuick ? '빠른 등록 닫기' : '빠른 등록'}
          </button>
        </div>

        {showQuick ? (
          <div className="inv-picker-modal__quick">
            <select
              value={quickMode}
              disabled={busy}
              onChange={(e) => {
                const mode = e.target.value as 'existing' | 'new';
                setQuickMode(mode);
                setQuickMasterId(null);
                setQuickName('');
              }}
            >
              <option value="new">신규 품명 등록</option>
              <option value="existing">기존 품명 + 규격 추가</option>
            </select>
            {quickMode === 'existing' ? (
              <select
                value={quickMasterId ?? ''}
                disabled={busy}
                onChange={(e) => {
                  const id = e.target.value ? Number(e.target.value) : null;
                  setQuickMasterId(id);
                  const master = masters.find((m) => m.masterId === id);
                  if (master?.defaultUnit) setQuickUnit(master.defaultUnit);
                }}
              >
                <option value="">품명 선택 *</option>
                {filteredMasters.map((m) => (
                  <option key={m.masterId} value={m.masterId}>
                    [{inventoryKindLabel(m.itemKind)}] {m.itemName}
                  </option>
                ))}
              </select>
            ) : (
              <>
                <select
                  value={quickKind}
                  disabled={busy}
                  onChange={(e) => setQuickKind(e.target.value as InventoryItemKind)}
                >
                  <option value="MATERIAL">{inventoryKindLabel('MATERIAL')}</option>
                  <option value="PART">{inventoryKindLabel('PART')}</option>
                  <option value="FINISHED">{inventoryKindLabel('FINISHED')}</option>
                </select>
                <input
                  placeholder="품명 *"
                  value={quickName}
                  disabled={busy}
                  onChange={(e) => setQuickName(e.target.value)}
                />
              </>
            )}
            <input
              placeholder={quickMode === 'existing' ? '새 규격 *' : '규격'}
              value={quickSpec}
              disabled={busy}
              onChange={(e) => setQuickSpec(e.target.value)}
            />
            <input
              placeholder="단위"
              value={quickUnit}
              disabled={busy}
              onChange={(e) => setQuickUnit(e.target.value)}
            />
            <button
              type="button"
              className="inv-picker-modal__quick-submit"
              disabled={busy}
              onClick={() => void handleQuickCreate()}
            >
              {quickSaving ? '등록 중…' : '등록 후 선택'}
            </button>
          </div>
        ) : null}

        <div className="inv-picker-modal__body">
          {items.length === 0 ? (
            <p className="inv-picker-modal__empty">
              등록된 재고 품목이 없습니다. 「빠른 등록」으로 추가하세요.
            </p>
          ) : filtered.length === 0 ? (
            <p className="inv-picker-modal__empty">검색 결과가 없습니다.</p>
          ) : (
            <div className="inv-picker-modal__table-wrap">
              <table className="inv-picker-modal__table">
                <thead>
                  <tr>
                    <th style={{ width: 72 }}>구분</th>
                    <th>품명</th>
                    <th>규격</th>
                    <th style={{ width: 64 }}>단위</th>
                    <th style={{ width: 88 }}>재고</th>
                    <th style={{ width: 88 }} />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <tr
                      key={row.inventoryId}
                      className={selectingId === row.inventoryId ? 'is-busy' : undefined}
                      onClick={() => {
                        if (!busy) void pickItem(row);
                      }}
                    >
                      <td>{inventoryKindLabel(row.itemKind)}</td>
                      <td>{row.itemName || '-'}</td>
                      <td>{row.specText?.trim() || '기본'}</td>
                      <td>{row.unit || '-'}</td>
                      <td>{formatInventoryQty(row.stockQty)}</td>
                      <td>
                        <button
                          type="button"
                          className="inv-picker-modal__pick"
                          disabled={busy}
                          onClick={(e) => {
                            e.stopPropagation();
                            void pickItem(row);
                          }}
                        >
                          {selectingId === row.inventoryId ? '선택 중…' : '선택'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default CompanyInventoryPickerModal;
